import type { DocSummary } from "./types.js";

/**
 * Pure list logic for the Documentation pages, kept out of the components
 * so it unit-tests directly. The folder page and the landing page both
 * read from here; neither re-derives a rule inline.
 */

/**
 * The documentation IS the library shelf (2026-09-14): a document pinned to
 * a release is release documentation and is read under that release on the
 * Releases page. portal-api stamps `shelf` on every row; a row from an older
 * serializer has none, and then the pin itself decides.
 */
export function isLibraryDoc(d: DocSummary): boolean {
  if (d.shelf) return d.shelf === "library";
  return d.default_release == null;
}

/**
 * The date an article is SHOWN and ORDERED by: its own detected date (an
 * uploaded PDF's /CreationDate) when there is one, else when it was
 * published, else when it was created. `null` only when every one of those is
 * missing or unparseable — which is a fixture, not a real row.
 */
export function articleDate(d: DocSummary): string | null {
  for (const iso of [d.document_date, d.published_at, d.created_at]) {
    if (iso && !Number.isNaN(new Date(iso).getTime())) return iso;
  }
  return null;
}

/** Newest first by `articleDate`; undated rows sink to the bottom and ties
 * keep their input order (stable). Returns a new array. */
export function sortNewestFirst(docs: readonly DocSummary[]): DocSummary[] {
  const time = (d: DocSummary): number | null => {
    const iso = articleDate(d);
    return iso ? new Date(iso).getTime() : null;
  };
  return docs
    .map((d, i) => ({ d, i, t: time(d) }))
    .sort((a, b) => {
      if (a.t == null && b.t == null) return a.i - b.i;
      if (a.t == null) return 1;
      if (b.t == null) return -1;
      if (a.t === b.t) return a.i - b.i;
      return b.t - a.t;
    })
    .map((x) => x.d);
}

export interface SolutionAreaOption {
  slug: string;
  name: string;
}

/** A doc's solution-area slugs (many-to-many). */
function docAreaSlugs(d: DocSummary): string[] {
  return (d.solution_areas ?? []).map((a) => a.slug);
}

/**
 * The solution areas a folder's documents actually span — the chips a folder
 * page offers, and only when there is more than one (a single-area folder
 * has nothing to switch between). Ordered as the managed list orders them,
 * with any area the managed list does not know appended by name; so the
 * chips read in the command center's order even before that list arrives.
 */
export function solutionAreasIn(
  docs: readonly DocSummary[],
  managed: ReadonlyArray<SolutionAreaOption> = [],
): SolutionAreaOption[] {
  const present = new Map<string, string>();
  for (const d of docs) for (const a of d.solution_areas ?? []) present.set(a.slug, a.name);

  const out: SolutionAreaOption[] = [];
  for (const m of managed) {
    if (present.has(m.slug)) {
      out.push({ slug: m.slug, name: present.get(m.slug) ?? m.name });
      present.delete(m.slug);
    }
  }
  const rest = [...present.entries()].map(([slug, name]) => ({ slug, name }));
  rest.sort((a, b) => a.name.localeCompare(b.name));
  return [...out, ...rest];
}

/** The chip filter. `"all"` passes everything through; otherwise a doc
 * matches when ANY of its areas is the selected slug. There is no
 * "Uncategorized" chip: an un-tagged article shows under All and nowhere
 * else, because "no solution area" is not a topic to browse. */
export function solutionFilter(docs: readonly DocSummary[], active: string): DocSummary[] {
  if (active === "all") return [...docs];
  return docs.filter((d) => docAreaSlugs(d).includes(active));
}

/** Per-chip counts — `all` plus one key per offered area (zero-filled). A
 * doc with N areas counts under EACH of them. */
export function solutionCounts(
  docs: readonly DocSummary[],
  areas: ReadonlyArray<SolutionAreaOption>,
): Record<string, number> {
  const counts: Record<string, number> = { all: docs.length };
  for (const a of areas) counts[a.slug] = 0;
  for (const d of docs) {
    for (const slug of docAreaSlugs(d)) {
      if (slug in counts) counts[slug] = (counts[slug] ?? 0) + 1;
    }
  }
  return counts;
}

/** The chips to render: All, then each area in order, each with its count. */
export function solutionOptions(
  counts: Record<string, number>,
  areas: ReadonlyArray<SolutionAreaOption>,
): { value: string; label: string; count: number }[] {
  return [
    { value: "all", label: "All", count: counts.all ?? 0 },
    ...areas.map((a) => ({ value: a.slug, label: a.name, count: counts[a.slug] ?? 0 })),
  ];
}
